/**
 * Real-time workspace events over SSE.
 *
 * One EventSource per browser tab, shared by every subscriber, replacing the
 * per-page polling intervals. The stream is workspace-scoped server-side, so a
 * listener only ever receives events it is authorized to see.
 */
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

let source = null;
let currentWorkspace = null;
let retry = 0;
let retryTimer = null;
const listeners = new Set();
const stateListeners = new Set();
let connected = false;

const EVENT_TYPES = [
  "connected",
  "job.created",
  "job.updated",
  "account.connected",
  "account.updated",
  "account.synced",
  "bot.created",
  "bot.updated",
  "bot.upserted",
  "bot.provisioned",
  "bot.update",
  "bot.webhook.configured",
  "journey.started",
  "journey.updated",
  "journey.completed",
  "journey.failed",
];

function setConnected(value) {
  if (connected === value) return;
  connected = value;
  stateListeners.forEach((fn) => fn(connected));
}

function emit(type, payload) {
  listeners.forEach((fn) => {
    try {
      fn({ type, ...payload });
    } catch {
      /* a broken listener must not tear down the stream */
    }
  });
}

function close() {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  if (source) source.close();
  source = null;
  setConnected(false);
}

function open() {
  const ws = localStorage.getItem("ws_id") || "";
  if (source && ws === currentWorkspace) return;
  close();
  currentWorkspace = ws;
  // The workspace also travels as a query param because EventSource cannot set
  // custom headers; the server still authorizes from the session cookie.
  const url = `${BASE}/events/stream${ws ? `?workspace_id=${encodeURIComponent(ws)}` : ""}`;
  source = new EventSource(url, { withCredentials: true });

  source.onopen = () => {
    retry = 0;
    setConnected(true);
  };
  source.onerror = () => {
    setConnected(false);
    if (source) source.close();
    source = null;
    // Exponential backoff, capped, so a backend restart does not hammer it.
    const delay = Math.min(1000 * 2 ** retry, 30000);
    retry += 1;
    retryTimer = setTimeout(open, delay);
  };
  EVENT_TYPES.forEach((type) => {
    source.addEventListener(type, (e) => {
      let parsed = {};
      try {
        parsed = JSON.parse(e.data);
      } catch {
        /* ignore malformed frame */
      }
      emit(type, parsed);
    });
  });
}

/** Subscribe to every workspace event. Returns an unsubscribe function. */
export function subscribeEvents(handler) {
  listeners.add(handler);
  open();
  return () => {
    listeners.delete(handler);
    if (listeners.size === 0 && stateListeners.size === 0) close();
  };
}

/** Subscribe to connection state (true/false). Returns an unsubscribe function. */
export function subscribeConnection(handler) {
  stateListeners.add(handler);
  handler(connected);
  open();
  return () => {
    stateListeners.delete(handler);
    if (listeners.size === 0 && stateListeners.size === 0) close();
  };
}

export function reconnectEvents() {
  close();
  open();
}
